// Wire-format types and API contract for PRO Deployment Management (PRD 06).
// Mirrors the shape used by `managementGuidelinesApi` so adapter / error
// patterns stay consistent across features. See:
//   docs/api-contracts/pro-deployment-management.md (frontend proposal).

// ────────────────────────────────────────────────────────────────────────────
// 2.1 Item pool entry — read-only on this page (canonical lives in PRD 04).

export type Frequency = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly';

export const FREQUENCIES: readonly Frequency[] = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
] as const;

// 공통 / 질병군 공통 / 질병 특화 — PRD 06 §3.1.
export type ItemScope = 'COMMON' | 'GROUP' | 'SPECIFIC';

export interface ProItemRef {
  pid: string;            // 'P-0001'. System-assigned at PRO Item Design (PRD 04 §4.1).
  name: string;
  questionCount: number;  // Sum of linked QIDs. PRD 04/05.
  required: boolean;      // Drives the '필수/선택' chip in the popover.
  freqCat: Frequency;     // Module bucket the item belongs to.
  area: string;           // 영역명 (e.g. '정서·인지 증상 관리').
  scope: ItemScope;
  diseaseGroup: string;   // '정신건강', '순환기', '내분비', … (PRD 08). Display only.
  diseaseCode: string;    // 'F32' when scope === 'SPECIFIC'; otherwise ''. Display only.
  // FEATURE_025 — the ids the page filters by. A group name is free text an administrator can rename, so
  // filtering by it hid rows whenever the two spellings drifted. GROUP: the item's group. SPECIFIC: the
  // code's group and the code. COMMON: both null. Optional only because the mock does not send them.
  diseaseGroupId?: number | null;
  diseaseCodeId?: number | null;
  // (ADM.006 — the item pool is 미배치-only, so no owner attribution is
  //  returned; the former `currentOwnerMid` field is obsolete and removed.)
  // SHCT-728 — does this item carry an undeployed change? Decided by the server, which compares the
  // current configuration against the last publish snapshot (`ad_pro_publish_*`), so the answer is the
  // same in a new tab and under another operator's login. Optional: the mock does not send it.
  dirty?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// 2.2 Module.

export type Weekday = 'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN';

export const WEEKDAYS: readonly Weekday[] = [
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
] as const;

export interface Module {
  mid: string;                                // 'M-0001'. System-assigned.
  name: string;                               // e.g. '매주(월) 모듈'.
  icon: string;                               // Emoji (prototype: 📋).
  frequency: Frequency;
  weekdays: Weekday[];                        // [] for daily / quarterly.
  frequencyLabel: string;                     // e.g. '매월 화요일 (1,5,9,13,17,21,25주)'.
  responseLabel: string;                      // e.g. '매일 필수 응답'.
  responseLabelType: 'primary' | 'warning';
  itemIds: string[];                           // Ordered (display order).
  areaOrder: string[];                         // Ordered area labels.
  // SHCT-728 — does this module hold an undeployed change? Server-decided, and it covers all three
  // sources PRD 06 §3.4 names (항목 · 문항 · 관리지침). The FE could only ever attribute the first: a
  // dirty 문항 is a QID and a dirty 관리지침 is a GID, and nothing on the client maps either back to a
  // module — which is exactly the case that caused the incident this ticket exists for.
  // Optional: absent in the mock; the real BE always sends it.
  dirty?: boolean;
  // Optimistic-lock version. The FE echoes it back as `expectedVersion` on the
  // next edit so BE can 409 (MODULE_VERSION_CONFLICT) on a concurrent write.
  // Optional: absent in the mock; the real BE always sends it.
  version?: number;
  // @deprecated — weekly load-warning removed per ADM.008. No longer rendered;
  // kept optional only until BE stops emitting it (BE hand-off note B2).
  note?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// 2.3 Deploy state + history.

// BE 2-chip code (DeployStateResponse.status): the wire sends `null` on first
// entry (no deploy history → FE shows no chip), else CHANGED (module edits
// persisted since the last deploy) or DEPLOYED. Matches the BE exactly (M7).
export type DeployStatus = 'CHANGED' | 'DEPLOYED';

export interface DeployState {
  // FEATURE_025 — judged for the selected row's LAYER; a layer never deployed and with nothing to deploy is null.
  status: DeployStatus | null;
  // 'YYYY.MM.DD HH:MM' KST. The selected layer's last deploy; the 전체 row shows the latest deploy of any layer.
  // null when there is none.
  lastDeployedAt: string | null;
  // FEATURE_025 — the layers ABOVE the selected row that still hold an undeployed change (common first, then the
  // code's group). Empty for 전체 and 공통. Optional: the mock and an older server do not send it.
  undeployedUpperLayers?: UpperLayer[];
}

// FEATURE_025 — one layer above the selected row. `groupName` is set for GROUP only.
export interface UpperLayer {
  layer: 'COMMON' | 'GROUP';
  groupName: string | null;
}

export interface DeploySummary {
  itemsCount: number;
  surveysCount: number;
  guidelinesCount: number;
  moduleConfigChanged: boolean;
}

export interface DeployHistoryEntry {
  deployedAt: string;        // 'YYYY.MM.DD HH:MM' KST.
  deployerName: string;
  deployerRole: string;      // ROOT, CONTENT, …
  // FEATURE_025 — the layer the deploy published, as the dropdown read it that day. null on rows written before
  // deploys recorded their layer. Optional: the mock does not always send it.
  target?: string | null;
  summary: DeploySummary;
}

// ────────────────────────────────────────────────────────────────────────────
// 2.4 Disease filter option.

export interface DiseaseFilterOption {
  value: string;              // 'ALL' | 'COMMON' | 'GRP:정신건강' | 'F32' | …
  label: string;
  group?: string;             // Disease-group name for ICD-coded entries.
  groupId?: number;           // FEATURE_025 — set on a group row and a code row.
  codeId?: number;            // FEATURE_025 — set on a code row only.
}

// ────────────────────────────────────────────────────────────────────────────
// 2.5 Current configuration — what the administrator is editing. Not the published snapshot: that word
// is kept for the copy a deploy writes.

export interface ProDeploymentCurrentConfig {
  diseaseFilter: string;
  monitoringWeeks: number;
  // FEATURE_025 — the ONE shared module set, identical for every dropdown row.
  modules: Module[];
  // Full PRO item dictionary. The page shows, counts and numbers only the items in the selected row's
  // scope (utils/disease-scope `itemInView`), and saves always send the module's full item list.
  items: ProItemRef[];
  // Status of the selected row, computed by the server (changed since that row's deployed copy).
  deploy: DeployState;
}

// ────────────────────────────────────────────────────────────────────────────
// 3. Request inputs.

export interface CurrentConfigQuery {
  disease?: string;           // §2.4 token. Default 'F32' (Q3).
  weeks?: number;             // 1..52. Default 26.
}

export interface ItemPoolQuery {
  disease: string;
  freq: Frequency;
  // SHCT-516 — the module's open weekday(s). The pool must match the module's
  // FULL cadence (frequency + weekday), since Weekly/Thu and Weekly/Sat are
  // distinct modules; matching frequency alone let a wrong-weekday item be
  // added. `[]` for daily / quarterly (no weekday).
  weekdays: Weekday[];
  // (ADM.006 — pool is 미배치-only; the former `excludeModuleId` "move" param
  //  is obsolete and removed.)
}

export interface UpdatePageConfigInput {
  diseaseFilter?: string;
  monitoringWeeks?: number;
}

export interface UpdateModuleInput {
  itemIds: string[];
  areaOrder: string[];
  // The module version the FE last read; BE 409s (MODULE_VERSION_CONFLICT) when
  // it no longer matches (another admin saved first). Omitted → last-write.
  expectedVersion?: number;
}

export interface DeployInput {
  summary: DeploySummary;
  // ADM.012 — the deploy audit row records which disease scope was published.
  // Sent alongside the summary so BE can pin the target disease into the audit
  // detail. Matches BE `DeployRequest.diseaseFilter` (nullable).
  diseaseFilter: string;
}

export interface DeployResult {
  deploy: DeployState;
  history: DeployHistoryEntry;
}

// ────────────────────────────────────────────────────────────────────────────
// 4. Error envelope.

export type ProDeploymentApiErrorCode =
  | 'MONITOR_WEEKS_OUT_OF_RANGE'
  | 'DISEASE_FILTER_REQUIRED'
  | 'ITEM_FREQ_MISMATCH'
  | 'ITEM_ALREADY_PLACED'
  | 'ITEM_NOT_IN_MODULE'
  | 'MODULE_VERSION_CONFLICT'
  | 'MODULE_NOT_FOUND'
  | 'ITEM_NOT_FOUND'
  | 'DEPLOY_FAILED'
  | 'INSUFFICIENT_PERMISSION'
  | 'UNAUTHORIZED'
  | 'INTERNAL_ERROR'
  | 'UNKNOWN';

export class ProDeploymentApiError extends Error {
  readonly code: ProDeploymentApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: ProDeploymentApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'ProDeploymentApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, ProDeploymentApiError.prototype);
  }
}

// ────────────────────────────────────────────────────────────────────────────
// 5. API surface.

export interface ProDeploymentApi {
  getCurrentConfig(query?: CurrentConfigQuery): Promise<ProDeploymentCurrentConfig>;
  listDiseaseOptions(): Promise<DiseaseFilterOption[]>;
  listItemPool(query: ItemPoolQuery): Promise<ProItemRef[]>;
  updatePageConfig(input: UpdatePageConfigInput): Promise<{
    diseaseFilter: string;
    monitoringWeeks: number;
  }>;
  updateModule(mid: string, input: UpdateModuleInput): Promise<Module>;
  deploy(input: DeployInput): Promise<DeployResult>;
  listHistory(limit?: number): Promise<DeployHistoryEntry[]>;
  // FEATURE_025 — how many started, unfinished questionnaires a deploy of this row would reset. null for a row
  // that cannot be deployed.
  getResetCount(disease: string): Promise<number | null>;
}
